import { useCallback, useEffect, useMemo } from 'react';

import { useUpdateEffect } from '@private/hooks';
import {
  keepPreviousData,
  QueryClient,
  QueryKey,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';

import { IAllPageFilters, IGetAllEntitiesConfig, IPageType } from 'utils/types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const normalizeQueryKey = (queryKey: any[]): (boolean | object | string)[] => {
  return queryKey.map((item) => {
    const itemType = typeof item;

    if (itemType === 'object' || itemType === 'string' || itemType === 'boolean') {
      return item;
    }

    return String(item);
  });
};

export const useQueryData = <T>(queryKey: string[]): null | T | undefined => {
  const queryClient = useQueryClient();

  const { data } = useQuery<null | T | undefined>({
    enabled: false,
    queryFn: () => {
      return queryClient.getQueryData(normalizeQueryKey(queryKey)) || null;
    },
    queryKey: normalizeQueryKey(queryKey),
  });
  return data;
};

interface IUseGetAllEntities<T> {
  config?: IGetAllEntitiesConfig;
  getAllEntitiesFunc: (filters: IAllPageFilters) => Promise<IPageType<T>>;
  isRefetch?: boolean;
  pageFilters: IAllPageFilters;
  queryDeps?: unknown[];
  queryKey: string | string[];
  select?: (data: IPageType<T>) => IPageType<T>;
}

export const useGetAllEntities = <T>({
  config = { enabled: true },
  getAllEntitiesFunc,
  isRefetch = true,
  pageFilters,
  queryDeps = [],
  queryKey: initialQueryKey,
  select,
}: IUseGetAllEntities<T>): UseQueryResult<IPageType<T>> => {
  const queryClient = useQueryClient();

  const queryKey = useMemo(
    () => (Array.isArray(initialQueryKey) ? initialQueryKey : [initialQueryKey]),
    [initialQueryKey],
  );
  const { page = 0 } = pageFilters;

  const getFunc = useCallback(
    (pageOffset = 0) =>
      () => {
        return getAllEntitiesFunc({
          ...pageFilters,
          page: page + pageOffset,
        });
      },
    [getAllEntitiesFunc, page, pageFilters],
  );

  const query = useQuery({
    queryFn: getFunc(),
    queryKey: normalizeQueryKey([...queryKey, page]),
    ...config,
    meta: { commitRecentPreset: true, pageKey: normalizeQueryKey([...queryKey, page]) },
    placeholderData: keepPreviousData,
    retry: 1,
    select,
    throwOnError: false,
  });

  useEffect(() => {
    if (query.isSuccess && query.data?.totalPages && !query.data?.last) {
      const nextPageData = queryClient.getQueryData(
        normalizeQueryKey([...queryKey, page + 1]),
      );
      if (nextPageData) {
        queryClient.resetQueries({
          queryKey: normalizeQueryKey([...queryKey, page + 1]),
        });
      }
      queryClient.prefetchQuery({
        queryFn: getFunc(1),
        queryKey: normalizeQueryKey([...queryKey, page + 1]),
      });
    }
  }, [query.isSuccess, query.data?.currentPage]);

  const refetchFn = () => {
    if (isRefetch && config.enabled) {
      query.refetch();
      queryClient.removeQueries({ queryKey: normalizeQueryKey([...queryKey, page - 1]) });
    }
  };

  useUpdateEffect(() => {
    refetchFn();
  }, queryDeps);

  useUpdateEffect(() => {
    refetchFn();
  }, [pageFilters]);

  return query;
};

interface IUseUpdateQueryData<T> {
  id?: number | string;
  queryClient: QueryClient;
  queryKey: (number | string)[];
  updatedItem: Partial<T>;
}

export const updateQueryData = <T extends { id: number }>({
  id,
  queryClient,
  queryKey,
  updatedItem,
}: IUseUpdateQueryData<T>): IPageType<T> | undefined => {
  if (!id) {
    return;
  }
  const dataPage = queryClient.getQueryData<IPageType<T>>(normalizeQueryKey(queryKey));

  queryClient.setQueryData(normalizeQueryKey(queryKey), () => ({
    ...dataPage,
    data: dataPage?.data?.map((item: T) =>
      item.id === id ? { ...item, ...updatedItem } : item,
    ),
  }));

  return dataPage;
};

/**
 * Optimistically patch a single inline-edited row across every cached grid page.
 * The grid list query key ends with a `page` segment and there can be several
 * cached pages (see `useGetAllEntities`), so pages are matched by predicate on
 * the shared `KEYS.ALL` sentinel rather than by reconstructing the exact key.
 * Returns the affected `[key, page]` snapshots so a failed save can restore them
 * via `revertInlineOptimisticRowUpdate` (inline-edit flicker fix).
 */
export const applyInlineOptimisticRowUpdate = <T extends { id: number }>({
  queryClient,
  rowId,
  sharedKey,
  updatedFields,
}: {
  queryClient: QueryClient;
  rowId?: number | string;
  sharedKey: string;
  updatedFields: Partial<T>;
}): [QueryKey, IPageType<T> | undefined][] => {
  const snapshots = queryClient.getQueriesData<IPageType<T>>({
    predicate: ({ queryKey }) => queryKey.includes(sharedKey),
  });

  queryClient.setQueriesData<IPageType<T>>(
    { predicate: ({ queryKey }) => queryKey.includes(sharedKey) },
    (page) =>
      page
        ? {
            ...page,
            data: page.data?.map((row) =>
              row.id === rowId ? { ...row, ...updatedFields } : row,
            ),
          }
        : page,
  );

  return snapshots;
};

export const revertInlineOptimisticRowUpdate = <T>({
  queryClient,
  snapshots,
}: {
  queryClient: QueryClient;
  snapshots?: [QueryKey, IPageType<T> | undefined][];
}): void => {
  snapshots?.forEach(([queryKey, page]) => {
    queryClient.setQueryData(queryKey, page);
  });
};
